/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import Factory from "App/render/webgl/passes/factory";
import WebGLContext from "App/web_gl_context";

import DepthShader from "App/render/webgl/shaders/depth/depth";
import ChunkLODDepth from "App/render/webgl/shaders/depth/chunk_lod_depth";
import AlphaDepth from "App/render/webgl/shaders/depth/alpha_depth";
import PointDepth from "App/render/webgl/shaders/depth/point_depth";
import LineDepth from "App/render/webgl/shaders/depth/line_depth";
import {
    AlphaTransparencyBinTypeName,
    BillboardBinTypeName,
    ChunkLODBinTypeName,
    TextBinTypeName,
    LinesBinTypeName,
    OpaqueBinTypeName,
    OpaqueNoTextureBinTypeName,
    OutlinesBinTypeName,
    PoiBinTypeName, PointsBinTypeName,
    TransparentBinTypeName,
    TransparentNoTextureBinTypeName, TransparentInternalBinTypeName, TransparentNoTextureInternalBinTypeName
} from "App/render/webgl/nodes/bins/type_names";
import {DepthPassTypeName} from "App/render/webgl/passes/type_names";
import IScene from "js_interface/src/scene_interface";

export default
class Depth extends Pass {

    public static TypeName: string = DepthPassTypeName;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);
    }


    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): Depth {
        return new Depth(aSceneAPI, aRenderGraph);
    }

    public initialize() {
        let shaders = this._renderGraph.getShaders();

        this._shadersToBins = {};

        this.attachShaderToBin(OpaqueBinTypeName, shaders.get(DepthShader.TypeName));
        this.attachShaderToBin(OpaqueNoTextureBinTypeName, shaders.get(DepthShader.TypeName));
        this.attachShaderToBin(ChunkLODBinTypeName, shaders.get(ChunkLODDepth.TypeName));
        this.attachShaderToBin(AlphaTransparencyBinTypeName, shaders.get(AlphaDepth.TypeName));
        this.attachShaderToBin(BillboardBinTypeName, shaders.get(DepthShader.TypeName));
        this.attachShaderToBin(TextBinTypeName, shaders.get(DepthShader.TypeName));
        this.attachShaderToBin(OutlinesBinTypeName, shaders.get(DepthShader.TypeName));
        this.attachShaderToBin(LinesBinTypeName, shaders.get(LineDepth.TypeName));
        this.attachShaderToBin(PoiBinTypeName, shaders.get(PointDepth.TypeName));
        this.attachShaderToBin(TransparentBinTypeName, shaders.get(DepthShader.TypeName));
        this.attachShaderToBin(TransparentNoTextureBinTypeName, shaders.get(DepthShader.TypeName));
        this.attachShaderToBin(TransparentInternalBinTypeName, shaders.get(DepthShader.TypeName));
        this.attachShaderToBin(TransparentNoTextureInternalBinTypeName, shaders.get(DepthShader.TypeName));
        this.attachShaderToBin(PointsBinTypeName, shaders.get(PointDepth.TypeName));
    }

    public readPoints(aView, aProperties) {
        let framebuffer = this._renderGraph.getFramebuffers().depth;

        framebuffer.bind();

        let points = [];
        for (let i = 0; i < aProperties.readPosition.length; i += 2) {
            points.push(
                framebuffer.readPoint(
                    aView,
                    aProperties.readPosition[i + 0],
                    aProperties.readPosition[i + 1])
            );
        }

        framebuffer.unbind();

        return points;
    }

    public render(aView) {
        let framebuffer = this._renderGraph.getFramebuffers().depth;

        let scale = framebuffer.parameters.scale;
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        // set rendering target
        if (framebuffer.hasValidContent(this._typeName)) {
            // if camera has not change its position, the buffer have valid data and do not need to be rendered
            // actually this is not 100% true as the buffers can be reused through different passes so the data can be invalidated by another usage
            // so the hasValidContent method checks if the rendered content is the same
            return;
        }

        framebuffer.bind();

        // set viewport
        gl.enable(gl.SCISSOR_TEST);
        gl.viewport(aView.position[0] * scale, aView.position[1] * scale, aView.size[0] * scale, aView.size[1] * scale);
        gl.scissor(aView.position[0] * scale, aView.position[1] * scale, aView.size[0] * scale, aView.size[1] * scale);

        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        // render all the bins related to the depth pass
        let bins = this._renderGraph.getRenderBins();

        this.view = aView;

        bins.get(OpaqueBinTypeName).render(this);
        bins.get(OpaqueNoTextureBinTypeName).render(this);
        bins.get(ChunkLODBinTypeName).render(this);
        bins.get(AlphaTransparencyBinTypeName).render(this);
        bins.get(BillboardBinTypeName).render(this);
        bins.get(TextBinTypeName).render(this);
        bins.get(OutlinesBinTypeName).render(this);
        bins.get(LinesBinTypeName).render(this);
        bins.get(PoiBinTypeName).render(this);

        // Commented out -> Be able to scroll through windows
        //bins.get(TransparentBinTypeName).render(this);
        //bins.get(TransparentNoTextureBinTypeName).render(this);

        bins.get(PointsBinTypeName).render(this);

        gl.disable(gl.SCISSOR_TEST);

        gl.clearColor(0.0, 0.0, 0.0, 0.0);

        framebuffer.unbind(this._typeName);

        this.view = null;
    }
}

Factory.register(Depth);
